import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import { Box, Typography } from "@mui/material";
import { useRef, useState } from "react";

import { ALLOWED_EXTENSIONS } from "../../constants/mime-map";
import { MAX_FILE_SIZE_BYTES } from "../../constants/limits";
import { formatFileSize } from "../../utils/formatFileSize";

type Props = {
  disabled?: boolean;
  onFilesSelected: (files: File[]) => void;
};

export function FileUploadZone({ disabled = false, onFilesSelected }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const accept = ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(",");
  const maxSizeLabel = formatFileSize(MAX_FILE_SIZE_BYTES);
  const extensionsLabel = ALLOWED_EXTENSIONS.join(", ");

  const handleFiles = (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0 || disabled) return;
    onFilesSelected(Array.from(fileList));
  };

  return (
    <Box
      onDragEnter={(event) => {
        event.preventDefault();
        if (!disabled) setIsDragOver(true);
      }}
      onDragOver={(event) => {
        event.preventDefault();
        if (!disabled) setIsDragOver(true);
      }}
      onDragLeave={(event) => {
        event.preventDefault();
        setIsDragOver(false);
      }}
      onDrop={(event) => {
        event.preventDefault();
        setIsDragOver(false);
        handleFiles(event.dataTransfer.files);
      }}
      onClick={() => {
        if (!disabled) inputRef.current?.click();
      }}
      sx={{
        mb: 2,
        minHeight: 148,
        borderRadius: 1,
        border: "1px dashed",
        borderColor: isDragOver ? "#1361A4" : "#D1D5DB",
        bgcolor: isDragOver ? "#F0F7FF" : "#FAFAFA",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        transition: "border-color 0.15s ease, background-color 0.15s ease",
      }}
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={accept}
        hidden
        disabled={disabled}
        onChange={(event) => {
          handleFiles(event.target.files);
          event.target.value = "";
        }}
      />

      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap={1}
        px={2}
        py={3}
        textAlign="center"
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            bgcolor: "#E8F2FB",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CloudUploadOutlinedIcon sx={{ fontSize: 28, color: "#1361A4" }} />
        </Box>

        <Typography fontSize={14} color="#374151">
          <Box component="span" sx={{ color: "#1361A4", fontWeight: 600 }}>
            Clique aqui
          </Box>{" "}
          ou arraste o arquivo para esta área para fazer o upload
        </Typography>

        <Typography fontSize={12} color="#9CA3AF">
          {extensionsLabel} — máx. {maxSizeLabel} por arquivo
        </Typography>
      </Box>
    </Box>
  );
}
